import { Link } from "react-router-dom";
import { useReveal } from "@/hooks/useReveal";
import { useLenis } from "@/components/SmoothScrollProvider";
import { CLUSTER_META } from "@/components/clusters/clusterMeta";

// Full current stock list (per owner, 2026-07). Items are [name, blurb] or
// [name, blurb, videoSlug] when the item maps to a 360° warehouse tour clip.
const GROUPS = [
  {
    heading: "Prints & patterns",
    items: [
      ["Nylon spandex prints", "Printed four-way stretch on a soft nylon base.", "v030"],
      ["Polyester spandex prints", "Colorfast prints that hold up wash after wash.", "v030"],
      ["Cotton spandex prints", "Natural-hand prints for tees and everyday wear.", "v017"],
      ["Mesh prints", "Patterned open knit for panels and overlays.", "v012"],
      ["Satin prints", "High-shine printed satin with stretch.", "v012"],
      ["Novelty spandex", "Foils, flocking, and specialty finishes, rotating stock.", "v013"],
      ["Sublimation paper prints", "Sublimation prints on paper, ready to heat-press.", "v030"],
    ],
  },
  {
    heading: "Core spandex & knits",
    items: [
      ["Nylon spandex", "Strong four-way stretch. The swim and activewear staple.", "v023"],
      ["Polyester spandex", "Durable, colorfast, and print-friendly.", "v023"],
      ["Cotton spandex", "Natural cotton hand with added recovery.", "v017"],
      ["Satin spandex", "Glossy face with full stretch. Dance and evening wear.", "v023"],
      ["Microfiber spandex", "Ultra-fine yarns with a smooth, dense hand.", "v017"],
      ["Tricot spandex", "Smooth, run-resistant warp knit.", "v023"],
      ["100% nylon tricot", "Classic tricot for linings and lingerie.", "v023"],
      ["Raschel spandex", "Textured raschel knit with firm hold.", "v023"],
      ["Woven spandex", "Stretch wovens for structured, tailored pieces.", "v017"],
      ["Wide-width spandex", "Extra-wide rolls. Bigger cuts, fewer seams.", "v012"],
      ["Bengaline spandex", "Ribbed stretch woven for pants and structure."],
      ["Fleece spandex", "Brushed-back warmth with stretch."],
      ["Sweater knits", "Cozy fashion knits in rotating styles.", "v035"],
    ],
  },
  {
    heading: "Performance & technical",
    items: [
      ["Athletic spandex", "Training-grade knits for team and performance wear.", "v033"],
      ["Supplex spandex", "Cotton feel, nylon performance. Breathable and quick-dry.", "v017"],
      ["Rayon CoolMax spandex", "Moisture-wicking CoolMax blend, quick to dry."],
      ["UV spandex", "Resists sun fade and breakdown. Outdoor and swim."],
      ["Antimicrobial spandex", "Odor-control finish for activewear."],
      ["Fire-retardant spandex", "FR-rated stretch for costume and industry."],
      ["Medical spandex", "Compression-grade knits for braces and supports."],
      ["US military fabric & spandex", "Mil-spec fabrics and stretch.", "v035"],
      ["Football spandex", "Heavy-duty stretch for uniforms and pads.", "v037"],
      ["Football mesh spandex", "Ventilated jersey mesh with stretch.", "v037"],
      ["Breathoprene spandex", "Breathable neoprene-style cushioning.", "v038"],
      ["Spacer spandex", "Three-dimensional double knit with a cushioned core.", "v038"],
      ["Techsheen spandex", "Sleek, high-gloss competition knit.", "v032"],
      ["Powernet spandex", "Strong compression net. Shapewear and control panels.", "v011"],
      ["High-compression 50% spandex blends", "Maximum hold for compression garments."],
    ],
  },
  {
    heading: "Mesh, lace, trims & more",
    items: [
      ["Mesh spandex", "Breathable stretch mesh for panels and layering.", "v033"],
      ["Sheer spandex", "Lightweight sheer stretch for overlays."],
      ["Stretch lace, all-over ridged", "Ridged all-over lace with built-in stretch.", "v034"],
      ["Stretch lace, edge band ridged", "Ridged edge-band lace for trims and finishes.", "v011"],
      ["Elastic bands, ¼″ to 14″ widths", "Knitted, braided, and woven, in many styles.", "v031"],
      ["Velcro spandex", "Hook-and-loop-receptive stretch."],
      ["Pound goods, spandex by the pound", "Mixed remnants by weight. The best value in the warehouse.", "v015"],
    ],
  },
];

const FabricGroup = ({ group }) => {
  const [ref, shown] = useReveal({ threshold: 0.06 });
  return (
    <div ref={ref} className="mt-14 first:mt-0">
      <p className="eyebrow text-[13px] text-kraft">{group.heading}</p>
      <ul
        data-testid="fabrics-grid"
        className="mt-4 grid grid-cols-1 gap-x-10 sm:grid-cols-2 lg:grid-cols-3"
      >
        {group.items.map(([name, blurb, videoSlug], i) => (
          <li
            key={name}
            data-testid="fabric-item"
            className={`border-t border-kraft/40 py-4 transition-[opacity,transform] duration-[600ms] ease-out ${
              shown ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
            }`}
            style={{ transitionDelay: shown ? `${i * 25}ms` : "0ms" }}
          >
            <p className="font-inter text-[15px] font-medium text-warmwhite">{name}</p>
            <p className="mt-1 font-inter text-sm leading-relaxed text-warmwhite/60">
              {blurb}
            </p>
            {videoSlug && (
              <Link
                to={`/warehouse?v=${videoSlug}`}
                aria-label={`See ${name} in the 360° warehouse tour`}
                className="eyebrow mt-1 inline-flex min-h-[44px] items-center py-2 text-[11px] text-kraft transition-colors duration-300 ease-out hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kraft"
              >
                See it in 360° →
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const FabricsSection = () => {
  const lenis = useLenis();
  const [headRef, headShown] = useReveal({ threshold: 0.2 });

  const goToContact = (e) => {
    e.preventDefault();
    const el = document.getElementById("contact");
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { duration: 1.2 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="fabrics" data-testid="section-fabrics" className="seam-top bg-indigo">
      <div className="mx-auto max-w-[1200px] px-5 py-[120px] md:px-7 lg:px-10">
        <div
          ref={headRef}
          className={`max-w-2xl transition-[opacity,transform] duration-[600ms] ease-out ${
            headShown ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"
          }`}
        >
          <p className="eyebrow text-xs text-kraft">What We Sell</p>
          <h2 className="font-display mt-5 text-3xl font-extrabold leading-[1.08] text-warmwhite md:text-[40px]">
            Types of fabric we carry.
          </h2>
          <p className="mt-6 max-w-xl font-inter text-base leading-[1.65] text-warmwhite/75">
            If it stretches, we probably stock it. Spandex and lycra in every
            base, nylon, polyester, cotton and satin, plus prints, meshes,
            laces, and performance goods, and plenty that never made it onto
            this page. Don&rsquo;t see what you need? Call us. Chances are
            it&rsquo;s on our floor.
          </p>
        </div>

        {/* Compact links to the fabric category pages (flagged for owner veto). */}
        <div
          data-testid="fabrics-cluster-links"
          className="mt-10 border-t border-kraft/40 pt-6"
        >
          <p className="eyebrow text-[11px] text-kraft">Shop by type</p>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3">
            {CLUSTER_META.map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                data-testid={`fabrics-cluster-link-${p.slug}`}
                className="eyebrow py-1 text-[12px] text-warmwhite/75 transition-colors duration-300 ease-out hover:text-warmwhite focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kraft"
              >
                {p.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-14">
          {GROUPS.map((group) => (
            <FabricGroup key={group.heading} group={group} />
          ))}
        </div>

        <a
          href="#contact"
          onClick={goToContact}
          data-testid="fabrics-contact-link"
          className="eyebrow mt-12 inline-flex min-h-[44px] items-center bg-warmwhite px-8 py-3 text-[13px] text-indigo transition-colors duration-300 ease-out hover:bg-kraft hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kraft"
        >
          Don&rsquo;t see it here? We probably have it. Call +1 718 781 4444
        </a>
      </div>
    </section>
  );
};
